import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import api from '../utils/api';
import SEO from '../components/SEO';
import ProductCard from '../components/ProductCard';

const HomePage = () => {




  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      // Simulate a slightly longer load time to show off the new loading screen
      // await new Promise(resolve => setTimeout(resolve, 1500)); 
      const response = await api.get('/products/featured');
      setFeaturedProducts(response.data);
    } catch (error) {
      console.error('Error fetching featured products:', error);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    // Only inject the script once the content (and the target div) is actually rendered
    if (!isLoading) {
      const script = document.createElement('script');
      script.src = 'https://embedsocial.com/cdn/ht.js';
      script.async = true;
      script.id = 'EmbedSocialScript';
      document.head.appendChild(script);

      // Clean up
      return () => {
        const existingScript = document.getElementById('EmbedSocialScript');
        if (existingScript) {
          existingScript.remove();
        }
      };
    }
  }, [isLoading]);


  return (
    <div className="min-h-screen bg-white font-['Jost'] font-light">
      <SEO
        description="Fresh, handpicked seasonal flowers delivered to your door in Addis Ababa. Subscribe or shop our collections."
      />
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(45, 85, 50, 0.75), rgba(35, 70, 40, 0.8)), url('/hero-flowers.jpg')`,
        }}
      >
        <div className="container mx-auto text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-light mb-4 sm:mb-6 leading-tight">
            <span className="text-yellow-200">Flowers that bring</span>
            <br />
            <span className="text-yellow-300">joy & love</span>
          </h1>
          <p className="text-lg sm:text-xl text-yellow-100 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Subscribe to fresh, handpicked seasonal flowers delivered to your door in Addis Ababa
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link
              to="/subscriptions"
              className="group relative bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 text-yellow-50 px-8 sm:px-10 py-4 sm:py-5 rounded-xl font-medium text-base sm:text-lg transition-all duration-300 inline-flex items-center justify-center shadow-xl hover:shadow-2xl hover:-translate-y-1"
            >
              <span className="relative z-10">View subscriptions</span>
              <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg" />
            </Link>
            <Link
              to="/shop"
              className="group relative bg-gradient-to-r from-amber-400 via-yellow-200 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-gray-950 px-8 sm:px-10 py-4 sm:py-5 rounded-xl font-normal text-base sm:text-lg transition-all duration-300 inline-flex items-center justify-center shadow-xl hover:shadow-2xl hover:-translate-y-1"
            >
              <span className="relative z-10">Shop now</span>
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg" />
            </Link>
            <Link
              to="/custom-bouquet"
              className="group relative bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 text-yellow-50 px-8 sm:px-10 py-4 sm:py-5 rounded-xl font-medium text-base sm:text-lg transition-all duration-300 inline-flex items-center justify-center shadow-xl hover:shadow-2xl hover:-translate-y-1"
            >
              <span className="relative z-10">Make your own bouquet</span>
              <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg" />
            </Link>
          </div>
        </div>
      </section>

      {/* Gradient Transition */}
      <div className="h-10 bg-gradient-to-b from-[rgba(35,70,40,0.8)] via-gray-50 to-white"></div>

      {/* Featured Products Section */}
      <section className="relative pt-8 pb-4 px-4 bg-white">
        {/* Decorative Background Pattern */}
        <div
          className="pointer-events-none absolute inset-0 z-0"
          aria-hidden="true"
        >
          <div className="absolute top-10 left-1/4 w-2 h-2 bg-emerald-300 rounded-full opacity-60" />
          <div className="absolute top-32 right-1/3 w-3 h-3 bg-yellow-300 rounded-full opacity-50" />
          <div className="absolute bottom-20 left-1/3 w-4 h-4 bg-green-300 rounded-full opacity-40" />
          <div className="absolute bottom-40 right-1/4 w-2 h-2 bg-lime-300 rounded-full opacity-70" />
          <div className="absolute top-1/2 left-10 w-6 h-6 bg-emerald-200 rounded-full opacity-30" />
        </div>

        {/* Content Wrapper */}
        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
              Our{" "}
              <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                collections
              </span>
            </h2>

            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Handpicked blooms that tell stories of beauty, crafted with love and
              delivered with care to create moments that last forever
            </p>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 lg:gap-12 py-8 px-2 sm:px-4">
            {isLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-200 rounded-2xl aspect-square mb-3" />
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                ))
              : featuredProducts.slice(0, 4).map((product, index) => (
                  <div key={product._id} className="group">
                    <div className="relative bg-white/50 backdrop-blur-md rounded-2xl sm:rounded-[2rem] p-2 sm:p-3 border border-white/40 transition-all duration-500 hover:-translate-y-2 hover:bg-white/60 shadow-sm hover:shadow-lg">
                      <ProductCard product={product} priority={index < 4} />
                    </div>
                  </div>
                ))
            }
          </div>

          {/* CTA */}
          <div className="text-center mt-6 mb-4">
            <Link
              to="/shop"
              onClick={() => window.scrollTo(0, 0)}
              className="group relative inline-flex items-center rounded-full bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 border border-emerald-400/30 px-10 py-4 text-lg font-normal text-white shadow-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl"
            >
              <span className="relative z-10">Explore all collections</span>
              <ArrowRight className="ml-3 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 relative z-10" />
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </Link>
          </div>
        </div>
      </section>

      {/* Gradient Transition */}
      <div className="h-6 bg-gradient-to-b from-white via-emerald-50/30 to-white"></div>

      {/* Google Reviews (EmbedSocial) */}
      <section className="relative pt-2 pb-4 px-4 bg-white">
        <div className="absolute top-10 left-10 w-16 h-16 bg-emerald-200 rounded-full opacity-30" />
        <div className="absolute bottom-10 right-10 w-12 h-12 bg-green-300 rounded-full opacity-40" />
        <div className="absolute top-1/3 right-1/4 w-8 h-8 bg-lime-300 rounded-full opacity-50" />

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                Google
              </span>{' '}
              reviews
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              See what our customers are saying about Brooks Flower on Google.
            </p>
          </div>

          <div className="flex justify-center mb-12">
            <a
              href="https://search.google.com/local/writereview?placeid=ChIJ0c1yBgCbSxYR9UudPc9pNKo"
              target="_blank"
              rel="noreferrer"
              className="group relative inline-flex items-center rounded-full bg-gradient-to-r from-emerald-500 to-green-600 px-10 py-4 text-lg font-normal text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              <span>Leave a review on Google</span>
            </a>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-emerald-100 p-4 sm:p-6 mb-8">
            <div
              className="embedsocial-hashtag"
              data-ref="1d71a3928cc0b05de19e63e153b3ab4dec5f6d97"
            >
              <a
                className="feed-powered-by-es"
                href="https://embedsocial.com/blog/embed-google-reviews/"
                target="_blank"
                rel="noreferrer"
                title="Embed Google reviews"
              >

              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/*<section className="relative py-20 px-4 bg-white overflow-hidden">

        <div className="absolute top-10 left-10 w-20 h-20 bg-emerald-200 rounded-full opacity-30"></div>
        <div className="absolute bottom-10 right-10 w-16 h-16 bg-green-300 rounded-full opacity-40"></div>
        <div className="absolute top-1/2 left-1/3 w-12 h-12 bg-lime-300 rounded-full opacity-50"></div>
        
        <div className="relative max-w-6xl mx-auto">
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-green-50 rounded-3xl transform rotate-1 group-hover:rotate-2 transition-transform duration-300"></div>
            <div className="relative bg-white rounded-3xl p-16 shadow-2xl border-2 border-emerald-100 text-center">

              <div className="relative inline-block mb-12">
                <div className="absolute -inset-6 bg-gradient-to-r from-emerald-400 to-green-400 rounded-full blur opacity-20"></div>
                <div className="relative bg-white border-4 border-emerald-100 rounded-full p-8 shadow-2xl">
                  <Gift className="w-16 h-16 text-emerald-600" />
                </div>
                <div className="absolute -top-3 -right-3 w-8 h-8 bg-yellow-400 rounded-full animate-pulse"></div>
                <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-green-400 rounded-full"></div>
              </div>
              
              <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
                Create Magic with
                <br />
                <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">Nature's Beauty</span>
              </h2>
              <p className="text-xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed">
                Step into a world where every petal tells a story, every arrangement speaks of love, and every delivery brings joy. 
                <span className="font-semibold text-emerald-600"> Let us help you create moments that bloom in hearts forever.</span>
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <Link
                  to="/shop"
                  onClick={() => window.scrollTo(0, 0)}
                  className="group relative bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white px-12 py-5 rounded-2xl font-bold text-lg transition-all duration-300 inline-flex items-center justify-center shadow-xl hover:shadow-2xl hover:-translate-y-2"
                >
                  <span className="relative z-10">Begin Your Journey</span>
                  <ArrowRight className="ml-4 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <Link
                  to="/subscriptions"
                  className="group bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 text-white px-12 py-5 rounded-2xl font-bold text-lg transition-all duration-300 inline-flex items-center justify-center shadow-xl hover:shadow-2xl hover:-translate-y-2"
                >
                  <span>Discover Subscriptions</span>
                  <Heart className="ml-3 w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>  */}
    </div>
  );
};

export default HomePage;
